import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check, ShieldCheck, Zap, HandshakeIcon, Phone } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { COMPANY } from "@/data/content";

const VANTAGGI = [
  { icon: ShieldCheck, t: "Coperture su misura", d: "Garanzie e wording calibrati sulla tua attività, dall'impresa edile al singolo artigiano." },
  { icon: Zap, t: "Analisi dedicata", d: "Un consulente valuta la tua esposizione reale al rischio e ti ricontatta entro 24 ore lavorative." },
  { icon: HandshakeIcon, t: "Broker indipendente", d: "Nessun conflitto d'interesse: confrontiamo più compagnie e scegliamo la soluzione migliore per te." },
];

const COPERTURE_RCT = [
  "RC verso Terzi (RCT) — danni a cose e persone",
  "RC verso Prestatori di Lavoro (RCO) — infortuni ai dipendenti e assimilati",
  "Danni da subappaltatori e cottimisti",
  "Committenza auto e mezzi d'opera",
  "Postuma su lavori ultimati",
  "RC crociata tra imprese associate",
];

const ALTRE_SOLUZIONI = [
  { t: "Gestione cantieri", d: "Appalti pubblici e privati: consulenza sui rischi contrattuali e sulle garanzie richieste dai capitolati." },
  { t: "CAR / EAR", d: "Contractor's / Erection All Risks: tutti i rischi del costruttore e dell'installatore su opere, impianti e materiali durante l'esecuzione." },
  { t: "Decennale Postuma", d: "Polizza obbligatoria D.Lgs. 122/2005 per la vendita di immobili di nuova costruzione a privati." },
  { t: "Rimpiazzo e Posa in Opera", d: "Copertura dei danni durante trasporto, montaggio e collaudo di impianti, macchinari e componenti." },
  { t: "Fidejussioni", d: "Garanzie di partecipazione, buona esecuzione, anticipazione e svincolo ritenute per gare e appalti." },
  { t: "RC Professionale Tecnica", d: "Per progettisti, direttori lavori e coordinatori sicurezza — obbligatoria per la partecipazione a gare pubbliche." },
  { t: "Tutela Legale d'Impresa", d: "Assistenza legale in ambito contrattuale, penale ambiente/sicurezza, recupero crediti." },
  { t: "All Risks Attrezzature", d: "Furto, incendio e rotture su macchinari, attrezzature, ponteggi, casseforme e veicoli speciali." },
];

const empty = {
  nome: "",
  email: "",
  telefono: "",
  codice_fiscale: "",
  partita_iva: "",
  sede_legale: "",
  attivita: "",
  website: "",
  oggetto: "Richiesta consulenza RC Edilizia/Artigiani",
  messaggio: "",
  privacy: false,
};

export default function RCTEdilizia() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 2) e.nome = "Inserisci un nome valido";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Inserisci un'email valida";
    if (form.telefono.trim().length < 6) e.telefono = "Inserisci un numero di telefono valido";
    const cf = form.codice_fiscale.trim();
    if (cf && !(/^[A-Za-z0-9]{11,16}$/).test(cf)) e.codice_fiscale = "CF non valido (11 o 16 caratteri)";
    const piva = form.partita_iva.trim();
    if (piva && !(/^\d{11}$/).test(piva)) e.partita_iva = "P.IVA composta da 11 cifre";
    if (!cf && !piva) e.codice_fiscale = "Inserisci almeno CF o Partita IVA";
    if (form.sede_legale.trim().length < 5) e.sede_legale = "Inserisci la sede legale (via, città, CAP)";
    if (form.attivita.trim().length < 20) e.attivita = "Descrivi con almeno 20 caratteri l'attività svolta";
    if (form.messaggio.trim().length < 10) e.messaggio = "Raccontaci brevemente la tua attività";
    if (!form.privacy) e.privacy = "Devi accettare l'informativa sulla privacy";
    setErrors(e); return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    // Honeypot: se compilato, è un bot — fingiamo il successo senza inviare nulla.
    if (form.website.trim()) {
      toast.success("Richiesta inviata", { description: "Un consulente XL Brokers ti richiama entro 24 ore lavorative." });
      setForm({ ...empty });
      return;
    }
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", { ...form, source: "Landing RCT Edilizia (Google Ads)" });
      toast.success("Richiesta inviata", { description: "Un consulente XL Brokers ti richiama entro 24 ore lavorative." });
      // GA event hook (safe if gtag not present)
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { form: "rct-edilizia" });
      }
      setForm({ ...empty });
    } catch (e) {
      toast.error("Errore", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-rct-edilizia" className="pt-28">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A1F2E] text-white">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1920&q=80"
            alt=""
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F2E] via-[#0A1F2E]/85 to-[#0A1F2E]/40" />
        </div>

        <div className="container-x py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-semibold text-[#89E877]">
              Assicurazioni per l'edilizia e l'artigianato
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Coperture RC su misura per <span className="italic text-[#89E877]">edilizia</span> e artigiani.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-white/75 leading-relaxed">
              La <strong className="text-white">Responsabilità Civile verso Terzi e Prestatori di Lavoro (RCT/O)</strong> è il presidio più importante per chi lavora in cantiere o presso i clienti.
              Che tu sia un'impresa edile, un idraulico, un elettricista o un installatore, XL Brokers analizza il tuo rischio reale e seleziona sul mercato le garanzie più ampie ai premi più competitivi — con l'indipendenza di un broker che lavora <strong className="text-white">solo per te</strong>.
            </p>

            <ul className="mt-10 grid sm:grid-cols-3 gap-6">
              {VANTAGGI.map(({ icon: Icon, t, d }) => (
                <li key={t} className="border-l-2 border-[#89E877] pl-4">
                  <Icon className="h-5 w-5 text-[#89E877]" />
                  <p className="mt-3 font-display text-lg">{t}</p>
                  <p className="mt-1 text-sm text-white/65 leading-relaxed">{d}</p>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#preventivo" data-testid="hero-cta-preventivo" className="inline-flex items-center gap-2 bg-[#89E877] text-[#0A1F2E] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-white transition-colors">
                Richiedi una consulenza <ArrowUpRight className="h-4 w-4" />
              </a>
              <a href={`tel:${COMPANY.phone}`} data-testid="hero-cta-phone" className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 text-sm font-medium tracking-wide hover:bg-white hover:text-[#0A1F2E] transition-colors">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-5" id="preventivo">
            <form onSubmit={submit} className="bg-background text-foreground p-8 sm:p-10 border-t-2 border-[#89E877]" data-testid="lp-form">
              <span className="overline">Richiesta di consulenza</span>
              <h2 className="mt-3 font-display text-3xl leading-tight">
                Ricevi un'analisi<br /><span className="italic text-primary">su misura</span>
              </h2>

              <div className="mt-8 space-y-6">
                {/* Honeypot anti-spam — invisibile agli utenti, lasciato vuoto */}
                <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
                  <label htmlFor="lp-website">Non compilare questo campo</label>
                  <input
                    id="lp-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="lp-nome">Ragione sociale / Nome*</label>
                  <input id="lp-nome" className="field-input" data-testid="lp-input-nome" value={form.nome} onChange={(e) => update("nome", e.target.value)} placeholder="Es. Rossi Costruzioni SRL" />
                  {errors.nome && <p className="mt-1 text-xs text-destructive">{errors.nome}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="lp-email">Email*</label>
                  <input id="lp-email" type="email" className="field-input" data-testid="lp-input-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="nome@azienda.it" />
                  {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="lp-tel">Telefono*</label>
                  <input id="lp-tel" className="field-input" data-testid="lp-input-telefono" value={form.telefono} onChange={(e) => update("telefono", e.target.value)} placeholder="+39 ..." />
                  {errors.telefono && <p className="mt-1 text-xs text-destructive">{errors.telefono}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label" htmlFor="lp-cf">Codice fiscale</label>
                    <input
                      id="lp-cf"
                      className="field-input"
                      data-testid="lp-input-codice-fiscale"
                      value={form.codice_fiscale}
                      onChange={(e) => update("codice_fiscale", e.target.value.toUpperCase())}
                      placeholder="RSSMRA80A01H501U"
                      maxLength={16}
                      autoComplete="off"
                    />
                    {errors.codice_fiscale && <p className="mt-1 text-xs text-destructive">{errors.codice_fiscale}</p>}
                  </div>
                  <div>
                    <label className="field-label" htmlFor="lp-piva">Partita IVA</label>
                    <input
                      id="lp-piva"
                      className="field-input"
                      data-testid="lp-input-partita-iva"
                      value={form.partita_iva}
                      onChange={(e) => update("partita_iva", e.target.value.replace(/\D/g, ""))}
                      placeholder="12345678901"
                      inputMode="numeric"
                      maxLength={11}
                      autoComplete="off"
                    />
                    {errors.partita_iva && <p className="mt-1 text-xs text-destructive">{errors.partita_iva}</p>}
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="lp-sede">Sede legale*</label>
                  <input
                    id="lp-sede"
                    className="field-input"
                    data-testid="lp-input-sede-legale"
                    value={form.sede_legale}
                    onChange={(e) => update("sede_legale", e.target.value)}
                    placeholder="Via Roma 10, 40128 Bologna (BO)"
                    autoComplete="street-address"
                  />
                  {errors.sede_legale && <p className="mt-1 text-xs text-destructive">{errors.sede_legale}</p>}
                </div>

                <div>
                  <label className="field-label" htmlFor="lp-attivita">Descrizione attività svolta*</label>
                  <textarea
                    id="lp-attivita"
                    rows={3}
                    className="field-input resize-none"
                    data-testid="lp-input-attivita"
                    value={form.attivita}
                    onChange={(e) => update("attivita", e.target.value)}
                    placeholder="Es. impianti idraulici e termosanitari per privati e condomini; cantieri prevalentemente in Emilia-Romagna."
                  />
                  {errors.attivita && <p className="mt-1 text-xs text-destructive">{errors.attivita}</p>}
                </div>

                <div>
                  <label className="field-label" htmlFor="lp-msg">Fatturato annuo e n. dipendenti*</label>
                  <textarea id="lp-msg" rows={3} className="field-input resize-none" data-testid="lp-input-messaggio" value={form.messaggio} onChange={(e) => update("messaggio", e.target.value)} placeholder="Es. Fatturato € 1,2 mln, 8 dipendenti, prevalentemente ristrutturazioni." />
                  {errors.messaggio && <p className="mt-1 text-xs text-destructive">{errors.messaggio}</p>}
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox id="lp-privacy" data-testid="lp-checkbox-privacy" checked={form.privacy} onCheckedChange={(v) => update("privacy", !!v)} className="mt-1" />
                  <label htmlFor="lp-privacy" className="text-xs text-muted-foreground leading-relaxed">
                    Accetto l'<Link to="/privacy" className="underline">informativa privacy</Link> ai sensi del GDPR (Reg. UE 2016/679).*
                  </label>
                </div>
                {errors.privacy && <p className="text-xs text-destructive">{errors.privacy}</p>}

                <button type="submit" disabled={loading} data-testid="lp-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#0A1F2E] text-white px-6 py-4 text-sm font-semibold tracking-wide hover:bg-primary transition-colors disabled:opacity-50">
                  {loading ? "Invio in corso..." : "Invia la richiesta"} <ArrowUpRight className="h-4 w-4" />
                </button>
                <p className="text-[11px] text-muted-foreground text-center">Senza impegno · Ti ricontattiamo entro 24 ore lavorative</p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* COPERTURE RCT */}
      <section className="section-y container-x">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="overline">Cosa copre</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">
              Le garanzie della<br /><span className="italic text-primary">polizza RCT/O</span>
            </h2>
            <p className="mt-6 text-muted-foreground max-w-md">
              Costruiamo il programma includendo di serie le estensioni che nel mercato si pagano spesso a parte.
            </p>
          </div>
          <ul className="md:col-span-8 grid sm:grid-cols-2 gap-5">
            {COPERTURE_RCT.map((c) => (
              <li key={c} className="flex items-start gap-4 border-b border-foreground/10 pb-4">
                <Check className="h-5 w-5 mt-1 text-primary shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ALTRE SOLUZIONI EDILIZIA */}
      <section className="bg-[#0A1F2E] text-white section-y">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-6">
              <span className="overline text-[#89E877]">Le altre soluzioni per edilizia e artigiani</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">
                Un unico partner<br />per tutti i rischi del tuo lavoro.
              </h2>
            </div>
            <p className="md:col-span-6 md:justify-self-end max-w-md text-white/70">
              Oltre alla RCT/O, progettiamo l'intero programma assicurativo dell'impresa o dell'artigiano: dal singolo cantiere alla decennale postuma.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {ALTRE_SOLUZIONI.map((s, i) => (
              <div key={s.t} data-testid={`soluzione-${i}`} className="bg-[#0A1F2E] p-8 hover:bg-[#0c2638] transition-colors">
                <p className="font-display text-4xl text-[#89E877]/40">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-6 font-display text-xl leading-tight">{s.t}</p>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#preventivo" className="inline-flex items-center gap-2 bg-[#89E877] text-[#0A1F2E] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-white transition-colors" data-testid="mid-cta">
              Richiedi analisi personalizzata <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* PROCESSO */}
      <section className="section-y container-x">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="overline">Come funziona</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">In 3 passi alla tua <span className="italic text-primary">consulenza</span>.</h2>
          </div>
          <ol className="md:col-span-8 space-y-10">
            {[
              { t: "Compili il modulo", d: "Ci bastano ragione sociale o nome, contatti e un'idea del tuo fatturato/numero addetti." },
              { t: "Ti ricontattiamo entro 24h", d: "Un consulente dedicato per approfondire attività, cantieri o lavori tipo e coperture già in essere." },
              { t: "Ricevi l'analisi", d: "Ti presentiamo il programma assicurativo più adatto, selezionato sul mercato. Senza impegno." },
            ].map((s, i) => (
              <li key={s.t} className="grid grid-cols-12 gap-6 border-t border-foreground/10 pt-8">
                <span className="col-span-2 md:col-span-1 font-display text-4xl text-primary/40">0{i + 1}</span>
                <div className="col-span-10 md:col-span-11">
                  <h3 className="font-display text-2xl">{s.t}</h3>
                  <p className="mt-2 text-muted-foreground">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-background border-t border-foreground/10 py-20">
        <div className="container-x grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">
              Pronto a proteggere la tua attività?
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl">
              Compila il modulo o chiamaci: affianchiamo ogni anno centinaia di imprese edili e artigiani in tutta Italia.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end flex flex-wrap gap-4">
            <a href="#preventivo" className="btn-primary" data-testid="final-cta-form">Richiedi consulenza</a>
            <a href={`tel:${COMPANY.phone}`} className="btn-outline" data-testid="final-cta-phone">
              <Phone className="h-4 w-4" /> Chiamaci
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
